import { NavLink, Outlet } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Clock, CalendarDays, MessageSquare, FileSpreadsheet, Users, Settings, Sparkles, CalendarCheck, ArrowLeftRight } from "lucide-react";
import { useMember } from "@/context/MemberContext";
import { Logo, RoleBadge } from "@/components/common";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/klokken", label: "Klokken", icon: Clock, id: "clock" },
  { to: "/planning", label: "Planning", icon: CalendarDays, id: "planning" },
  { to: "/prikbord", label: "Prikbord", icon: MessageSquare, id: "board" },
  { to: "/uren", label: "Uren", icon: FileSpreadsheet, id: "timesheet" },
  { to: "/ruilen", label: "Ruilen", icon: ArrowLeftRight, id: "swaps" },
  { to: "/beschikbaarheid", label: "Beschikbaarheid", icon: CalendarCheck, id: "availability" },
  { to: "/assistent", label: "AI-assistent", icon: Sparkles, id: "assistant", manager: true },
  { to: "/medewerkers", label: "Medewerkers", icon: Users, id: "employees", manager: true },
  { to: "/instellingen", label: "Instellingen", icon: Settings, id: "settings" },
];

const sideCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
    isActive ? "bg-white/15 text-white" : "text-emerald-100/80 hover:bg-white/10 hover:text-white"
  }`;

const Sidebar = ({ items, business, member }) => (
  <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#0D5C46] grain text-white p-5" data-testid="sidebar">
    <Logo light />
    <div className="mt-6 rounded-xl bg-white/10 p-3">
      <p className="text-[11px] uppercase tracking-wider text-emerald-100/70">Bedrijf</p>
      <p className="font-semibold truncate" data-testid="sidebar-business-name">{business.name}</p>
    </div>
    <nav className="mt-6 flex-1 space-y-1">
      {items.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} className={sideCls} data-testid={`nav-${n.id}`}>
          <n.icon className="h-[18px] w-[18px]" /> {n.label}
        </NavLink>
      ))}
    </nav>
    <div className="flex items-center gap-3 rounded-xl bg-white/10 p-3">
      <UserButton />
      <div className="min-w-0">
        <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{member.name}</p>
        <RoleBadge role={member.role} />
      </div>
    </div>
  </aside>
);

const bottomCls = ({ isActive }) =>
  `flex flex-1 flex-col items-center gap-1 py-2 text-[11px] font-medium transition-colors ${
    isActive ? "text-[#0D5C46]" : "text-slate-500"
  }`;

const MobileNav = ({ items }) => (
  <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-lg border-t border-slate-200 pb-safe flex" data-testid="bottom-nav">
    {items.slice(0, 5).map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} className={bottomCls} data-testid={`bottom-nav-${n.id}`}>
        <n.icon className="h-5 w-5" /> {n.label}
      </NavLink>
    ))}
  </nav>
);

const MobileHeader = ({ items, business }) => (
  <header className="md:hidden sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-slate-200 px-4 h-14 flex items-center justify-between" data-testid="mobile-header">
    <div className="min-w-0">
      <Logo compact />
    </div>
    <div className="flex items-center gap-0.5">
      {items.slice(5).map((n) => (
        <NavLink key={n.to} to={n.to} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100" data-testid={`mobile-nav-${n.id}`} aria-label={n.label}>
          <n.icon className="h-5 w-5" />
        </NavLink>
      ))}
      <div className="ml-1"><UserButton /></div>
    </div>
    <span className="sr-only">{business.name}</span>
  </header>
);

export default function AppShell() {
  const { member, business, isManager } = useMember();
  const items = NAV.filter((n) => !n.manager || isManager);
  return (
    <div className="min-h-screen bg-background">
      <Sidebar items={items} business={business} member={member} />
      <MobileHeader items={items} business={business} />
      <main className="md:pl-64">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-28 md:pb-10">
          <Outlet />
        </div>
      </main>
      <MobileNav items={items} />
    </div>
  );
}
